import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { getRCASettings, upsertRCASettings } from "api/queries/RCA/RCASettingsAPI";
import {
    IFailureTypes,
    IRCASettingsReturn,
    IRCASettingsByProductionLine,
    IRCASettingsEntity,
    IRCASettingsOwners,
    IRCAFrequencyTriggerUnit,
} from "./RCASettingsEntityInterfaces";
import { USERTYPE } from "constants/constants";

export const RCASettingsEntity: IRCASettingsEntity = {
    GetRCASettings: (userTypeIds?: number[]) => {
        const { data, error, loading, refetch } = useQuery(getRCASettings, {
            variables: {
                ...(userTypeIds
                    ? {
                          whereUser: {
                              user_type_id: { _in: userTypeIds },
                          },
                      }
                    : {}),
            },
        });

        const [rcaSettings, setRCASettings] = useState<IRCASettingsReturn[]>([]);

        useEffect(() => {
            if (data?.production_line && data?.failure_type && data?.user && data?.rca_frequency_trigger_unit) {
                const rcaSettingsByProductionLines: IRCASettingsByProductionLine[] = data.production_line;
                const failureTypes: IFailureTypes[] = data.failure_type;
                const ownersOptions: IRCASettingsOwners[] = data.user;
                const frequencyTriggerUnitOptions: IRCAFrequencyTriggerUnit[] = data.rca_frequency_trigger_unit;

                const rcaSettingsFormatted: IRCASettingsReturn[] = rcaSettingsByProductionLines.map(
                    (rcaSettingsByProductionLine) => ({
                        production_line_id: rcaSettingsByProductionLine.production_line_id,
                        name: rcaSettingsByProductionLine.name,
                        rca_settings: failureTypes.map((failureType) => {
                            const existingFailureTypeSettings = rcaSettingsByProductionLine.rca_settings.findIndex(
                                (rcaSetting) => rcaSetting.failure_type.failure_type_id === failureType.failure_type_id,
                            );

                            return existingFailureTypeSettings === -1
                                ? {
                                      owners_options: ownersOptions.filter(
                                          (owner) =>
                                              owner.mill_id === rcaSettingsByProductionLine.mill?.mill_id ||
                                              owner.user_type_id === USERTYPE.ADMINISTRATOR,
                                      ),
                                      frequency_trigger_unit_options: frequencyTriggerUnitOptions,
                                      failure_type: {
                                          failure_type_id: failureType.failure_type_id,
                                          name: failureType.name,
                                          default_failure_type_translations:
                                              failureType.default_failure_type_translations,
                                      },
                                      owner_id: null,
                                      rca_duration_trigger: {
                                          is_enabled: false,
                                          value: 0,
                                      },
                                      rca_frequency_trigger: {
                                          is_enabled: false,
                                          value: 0,
                                          rca_frequency_trigger_unit_id: null,
                                      },
                                      production_line_id: rcaSettingsByProductionLine.production_line_id,
                                  }
                                : {
                                      frequency_trigger_unit_options: frequencyTriggerUnitOptions,
                                      owners_options: ownersOptions.filter(
                                          (owner) =>
                                              owner.mill_id === rcaSettingsByProductionLine.mill?.mill_id ||
                                              owner.user_type_id === USERTYPE.ADMINISTRATOR,
                                      ),
                                      ...rcaSettingsByProductionLine.rca_settings[existingFailureTypeSettings],
                                      ...(rcaSettingsByProductionLine.rca_settings[existingFailureTypeSettings]
                                          ?.rca_duration_trigger
                                          ? {
                                                rca_duration_trigger:
                                                    rcaSettingsByProductionLine.rca_settings[
                                                        existingFailureTypeSettings
                                                    ]?.rca_duration_trigger,
                                            }
                                          : { rca_duration_trigger: { is_enabled: false, value: 0 } }),
                                      ...(rcaSettingsByProductionLine.rca_settings[existingFailureTypeSettings]
                                          ?.rca_frequency_trigger
                                          ? {
                                                rca_frequency_trigger:
                                                    rcaSettingsByProductionLine.rca_settings[
                                                        existingFailureTypeSettings
                                                    ]?.rca_frequency_trigger,
                                            }
                                          : {
                                                rca_frequency_trigger: {
                                                    is_enabled: false,
                                                    value: 0,
                                                    rca_frequency_trigger_unit_id: null,
                                                },
                                            }),
                                      production_line_id: rcaSettingsByProductionLine.production_line_id,
                                  };
                        }),
                    }),
                );
                setRCASettings(rcaSettingsFormatted);
            }
        }, [data]);

        return { apolloQueryError: error, rcaSettings, loading, refetch, setRCASettings };
    },
    UpsertRCASettings: () => {
        const [upsertRCASettingsData, { error, loading }] = useMutation(upsertRCASettings);
        return { error, upsertRCASettingsData, loading };
    },
};
